/**
 * Bilt Passport vinyl sticker renderer.
 *
 * Two portrait sizes only (2 in and 3 in wide). Same layout on both:
 * QR on top, official Bilt lockup (B-mark + wordmark) under it at QR width.
 *
 * Solid black field. White QR. White lockup. Print at 300 DPI.
 */
import { BILT_LOCKUP_WHITE_SRC } from './biltBrandDisplay';

export type PassportStickerFormat = 'portrait_2in' | 'portrait_3in';

export { BILT_LOCKUP_WHITE_SRC };

/** @deprecated Prefer BILT_LOCKUP_WHITE_SRC — stickers use the full lockup, not the badge. */
export { BILT_BADGE_SRC } from './biltBrandDisplay';

export interface PassportStickerSpec {
  width: number;
  height: number;
  label: string;
  physicalSize: string;
  useCase: string;
  dpi: 300;
  recommended: boolean;
  /** Physical width in inches (both formats are portrait: taller than wide). */
  widthInches: 2 | 3;
}

export const PASSPORT_STICKER_FORMATS: Record<PassportStickerFormat, PassportStickerSpec> = {
  portrait_3in: {
    width: 900,
    height: 1350,
    label: '3 in',
    physicalSize: '3 x 4.5 in',
    useCase: 'Side window, display board',
    dpi: 300,
    recommended: true,
    widthInches: 3,
  },
  portrait_2in: {
    width: 600,
    height: 900,
    label: '2 in',
    physicalSize: '2 x 3 in',
    useCase: 'Toolbox, glass, tight spaces',
    dpi: 300,
    recommended: false,
    widthInches: 2,
  },
};

export const PASSPORT_STICKER_PRINT = {
  dpi: 300,
  bleedInches: 0.125,
  safeInches: 0.125,
  quietZoneModules: 4,
  material: 'Matte outdoor automotive vinyl, UV laminate',
  inks: 'White + orange on black vinyl (or black vinyl with white+orange print)',
} as const;

/** Official lockup aspect (B-mark + BiLT + :SPEC). From brand/source/bilt-spec-lockup-master.png. */
export const BILT_LOCKUP_ASPECT = 1024 / 202;

const FONT = "-apple-system, 'Helvetica Neue', Arial, sans-serif";
const BLACK = '#000000';
const WHITE = '#f5f5f5';

export interface PassportStickerVehicleLabel {
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  nickname?: string | null;
}

/** Human-readable footer line for vinyl (shop, club, build, checkpoint, etc.). */
export function formatPassportStickerSubjectLabel(input: {
  shopName?: string | null;
  clubName?: string | null;
  eventName?: string | null;
  checkpointName?: string | null;
  vehicle?: PassportStickerVehicleLabel | null;
  fallback?: string | null;
}): string {
  const pick = (value?: string | null) => (value ?? '').trim();
  const shop = pick(input.shopName);
  if (shop) return shop;
  const club = pick(input.clubName);
  if (club) return club;
  const checkpoint = pick(input.checkpointName);
  if (checkpoint) return checkpoint;
  const eventName = pick(input.eventName);
  if (eventName) return eventName;

  const vehicle = input.vehicle;
  if (vehicle) {
    const ymm = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
      .filter((part) => part != null && String(part).trim() !== '')
      .join(' ')
      .trim();
    const nick = pick(vehicle.nickname);
    if (nick && ymm && nick.toLowerCase() !== ymm.toLowerCase()) {
      return `${ymm} · ${nick}`;
    }
    return nick || ymm;
  }

  return pick(input.fallback);
}

/** Reject short hex token fingerprints masquerading as footer labels. */
export function looksLikePassportTokenFingerprint(value?: string | null): boolean {
  const trimmed = (value ?? '').trim();
  return trimmed !== '' && /^[0-9A-F]{8,12}$/i.test(trimmed);
}

export function normalizePassportStickerSubjectLabel(value?: string | null): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed || looksLikePassportTokenFingerprint(trimmed)) {
    return null;
  }
  return trimmed;
}

export interface PassportStickerOptions {
  format: PassportStickerFormat;
  qrImage: HTMLImageElement;
  /** Official white Bilt lockup (transparent). Falls back to tracked BILT type if missing. */
  brandLockup?: HTMLImageElement | null;
  /** Footer label (shop, club, build name). Rendered all caps. */
  subjectLabel?: string | null;
}

/** Fallback only when the lockup asset fails to load. */
function drawWordmarkFallback(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  ctx.save();
  ctx.fillStyle = WHITE;
  ctx.font = `600 ${size}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const tracking = Math.round(size * 0.22);
  const chars = 'BILT'.split('');
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((sum, w) => sum + w, 0) + tracking * 3;
  let cursor = x - total / 2;
  chars.forEach((c, i) => {
    ctx.fillText(c, cursor + widths[i] / 2, y);
    cursor += widths[i] + tracking;
  });
  ctx.restore();
}

function drawLockup(
  ctx: CanvasRenderingContext2D,
  lockup: HTMLImageElement | null | undefined,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  if (lockup && lockup.naturalWidth > 0) {
    ctx.drawImage(lockup, x, y, width, height);
    return;
  }
  drawWordmarkFallback(ctx, x + width / 2, y + height / 2, Math.round(height * 0.72));
}

function fitSubjectLabel(
  ctx: CanvasRenderingContext2D,
  value: string,
  fontSize: number,
  maxWidth: number
): string {
  const upper = value.trim().toUpperCase();
  if (!upper) return '';
  ctx.font = `500 ${fontSize}px ${FONT}`;
  if (ctx.measureText(upper).width <= maxWidth) return upper;
  let trimmed = upper;
  while (trimmed.length > 1 && ctx.measureText(trimmed).width > maxWidth) {
    trimmed = trimmed.slice(0, -1);
  }
  return trimmed;
}

function drawSubjectLabel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  maxWidth: number,
  value: string
): void {
  const line = fitSubjectLabel(ctx, value, size, maxWidth);
  if (!line) return;
  ctx.save();
  ctx.fillStyle = WHITE;
  ctx.globalAlpha = 0.28;
  ctx.font = `500 ${size}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(line, x, y);
  ctx.restore();
}

function fillBlack(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.fillStyle = BLACK;
  ctx.fillRect(0, 0, w, h);
}

function createOpaqueCanvas(width: number, height: number): {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
} {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    throw new Error('Canvas is not available');
  }
  fillBlack(ctx, width, height);
  return { canvas, ctx };
}

/** Composite raster/SVG images onto solid black so PNG export has no transparent holes. */
async function flattenImageOnBlack(img: HTMLImageElement): Promise<HTMLImageElement> {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) {
    return img;
  }
  const { canvas, ctx } = createOpaqueCanvas(width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvasToImage(canvas);
}

function canvasToImage(canvas: HTMLCanvasElement): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not flatten image'));
    img.src = canvas.toDataURL('image/png');
  });
}

function resolveSubjectLabel(options: PassportStickerOptions): string | null {
  return normalizePassportStickerSubjectLabel(options.subjectLabel);
}

/**
 * Portrait sticker: QR on top, Bilt lockup under it spanning the QR width.
 */
function drawPortrait(ctx: CanvasRenderingContext2D, spec: PassportStickerSpec, options: PassportStickerOptions): void {
  const { width: w, height: h } = spec;
  fillBlack(ctx, w, h);

  const pad = Math.round(w * 0.1);
  const gap = Math.round(w * 0.07);
  const subjectLabel = resolveSubjectLabel(options);
  const labelH = subjectLabel ? Math.round(w * 0.05) : 0;
  const bottomPad = Math.round(w * 0.08) + labelH;

  const maxQrByWidth = Math.round(w - pad * 2);
  const availableH = h - pad - gap - bottomPad;
  const maxQrByHeight = Math.floor(availableH / (1 + 1 / BILT_LOCKUP_ASPECT));
  const qrSize = Math.min(maxQrByWidth, maxQrByHeight);

  const lockupW = qrSize;
  const lockupH = Math.round(lockupW / BILT_LOCKUP_ASPECT);

  const stackH = qrSize + gap + lockupH;
  const stackTop = Math.round(pad + (h - pad - bottomPad - stackH) * 0.4);
  const qrX = Math.round((w - qrSize) / 2);
  const qrY = stackTop;

  ctx.fillStyle = BLACK;
  ctx.fillRect(qrX, qrY, qrSize, qrSize);
  ctx.drawImage(options.qrImage, qrX, qrY, qrSize, qrSize);

  const lockupX = qrX;
  const lockupY = qrY + qrSize + gap;
  ctx.fillRect(lockupX, lockupY, lockupW, lockupH);
  drawLockup(ctx, options.brandLockup, lockupX, lockupY, lockupW, lockupH);

  if (subjectLabel) {
    drawSubjectLabel(
      ctx,
      w / 2,
      h - Math.round(w * 0.055),
      Math.round(w * 0.032),
      w - pad * 2,
      subjectLabel
    );
  }
}

export async function loadSvgImage(svg: string): Promise<HTMLImageElement> {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not load QR image'));
      img.src = url;
    });
    return flattenImageOnBlack(img);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Load the official white lockup for dark Passport vinyl. Returns null on failure. */
export function loadPassportBrandLockup(
  src: string = BILT_LOCKUP_WHITE_SRC
): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      void flattenImageOnBlack(img)
        .then((flat) => resolve(flat))
        .catch(() => resolve(img));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** @deprecated Use loadPassportBrandLockup */
export const loadPassportBadgeMark = loadPassportBrandLockup;

export async function renderPassportSticker(options: PassportStickerOptions): Promise<Blob> {
  const spec = PASSPORT_STICKER_FORMATS[options.format];
  const { canvas, ctx } = createOpaqueCanvas(spec.width, spec.height);
  drawPortrait(ctx, spec, options);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not export sticker'));
    }, 'image/png');
  });
}
